import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/axios';
import { MessageCircle, Save, TestTube, CheckCircle2, XCircle, Loader2, ExternalLink, Copy, Shield, Eye, EyeOff } from 'lucide-react';
import { TitleWithHelp } from '../components/ui/help-tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppConfig() {
  const [config, setConfig] = useState({
    phone_number_id: '',
    business_account_id: '',
    access_token: '',
    webhook_verify_token: '',
    business_phone_number: '',
    app_secret: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/whatsapp/config');
      const data = res.data;
      setConfigured(data.configured);
      setConfig(prev => ({
        ...prev,
        phone_number_id: data.phone_number_id || '',
        business_account_id: data.business_account_id || '',
        business_phone_number: data.business_phone_number || '',
        webhook_verify_token: data.webhook_verify_token || '',
        access_token: '',
        app_secret: '',
      }));
    } catch (err) {
      console.error('Erro ao carregar config WhatsApp:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.phone_number_id || !config.business_account_id) {
      toast.error('Phone Number ID e Business Account ID são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      await api.put('/whatsapp/config', config);
      toast.success('Configuração salva com sucesso');
      setConfigured(true);
      setTestResult(null);
      // Limpar campos sensíveis após salvar
      setConfig(prev => ({ ...prev, access_token: '', app_secret: '' }));
      await loadConfig();
    } catch (err) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/whatsapp/test-connection');
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Conexão estabelecida com sucesso!');
      } else {
        toast.error('Falha na conexão: ' + (res.data.error || 'Erro desconhecido'));
      }
    } catch (err) {
      setTestResult({ success: false, error: 'Erro ao testar conexão' });
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  const webhookUrl = `${BACKEND_URL}/api/whatsapp/webhook`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="whatsapp-config-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="whatsapp-config-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-green-100 rounded-lg">
          <MessageCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <TitleWithHelp
            title="WhatsApp Business"
            description="Configure a integração com a API oficial do WhatsApp Business para enviar e receber mensagens diretamente pelo CRM."
            connections={['CRM', 'Clientes', 'Oportunidades']}
          >
            <h1 className="text-2xl font-bold text-gray-900" data-testid="whatsapp-config-title">WhatsApp Business</h1>
          </TitleWithHelp>
          <p className="text-sm text-gray-500">Configure a integração com a API oficial do WhatsApp Business</p>
        </div>
        {configured && (
          <span className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" data-testid="whatsapp-status-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> Configurado
          </span>
        )}
      </div>

      {/* Webhook URL */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" />
            URL do Webhook
          </CardTitle>
          <CardDescription>
            Configure esta URL no painel da Meta para receber mensagens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm font-mono text-gray-700 select-all" data-testid="webhook-url">
              {webhookUrl}
            </code>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(webhookUrl)}
              data-testid="copy-webhook-url-btn"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            No painel da Meta: App Dashboard &gt; WhatsApp &gt; Configuration &gt; Webhook URL
          </p>
        </CardContent>
      </Card>

      {/* Credenciais */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Credenciais da API</CardTitle>
          <CardDescription>
            Obtidas no{' '}
            <a
              href="https://developers.facebook.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              Meta for Developers <ExternalLink className="w-3 h-3" />
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phone_number_id">Phone Number ID *</Label>
              <Input
                id="phone_number_id"
                data-testid="input-phone-number-id"
                placeholder="Ex: 123456789012345"
                value={config.phone_number_id}
                onChange={e => setConfig(p => ({ ...p, phone_number_id: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="business_account_id">Business Account ID (WABA) *</Label>
              <Input
                id="business_account_id"
                data-testid="input-business-account-id"
                placeholder="Ex: 987654321098765"
                value={config.business_account_id}
                onChange={e => setConfig(p => ({ ...p, business_account_id: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="business_phone_number">Telefone da Empresa</Label>
              <Input
                id="business_phone_number"
                data-testid="input-business-phone"
                placeholder="Ex: 5511999999999"
                value={config.business_phone_number}
                onChange={e => setConfig(p => ({ ...p, business_phone_number: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="webhook_verify_token">Webhook Verify Token</Label>
              <Input
                id="webhook_verify_token"
                data-testid="input-verify-token"
                placeholder="Token de verificação do webhook"
                value={config.webhook_verify_token}
                onChange={e => setConfig(p => ({ ...p, webhook_verify_token: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="access_token">Access Token (Permanente)</Label>
            <div className="relative">
              <Input
                id="access_token"
                data-testid="input-access-token"
                type={showToken ? 'text' : 'password'}
                placeholder={configured ? '(token salvo - deixe vazio para manter)' : 'Cole o token de acesso permanente'}
                value={config.access_token}
                onChange={e => setConfig(p => ({ ...p, access_token: e.target.value }))}
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="app_secret">App Secret (para validação de webhook)</Label>
            <div className="relative">
              <Input
                id="app_secret"
                data-testid="input-app-secret"
                type={showSecret ? 'text' : 'password'}
                placeholder={configured ? '(secret salvo - deixe vazio para manter)' : 'App Secret da Meta'}
                value={config.app_secret}
                onChange={e => setConfig(p => ({ ...p, app_secret: e.target.value }))}
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowSecret(!showSecret)}
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-config-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Configuração
            </Button>
            {configured && (
              <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-connection-btn">
                {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                Testar Conexão
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resultado do teste */}
      {testResult && (
        <Card className={testResult.success ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'} data-testid="test-result-card">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              {testResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              )}
              <div>
                <p className="font-medium text-sm">
                  {testResult.success ? 'Conexão estabelecida' : 'Falha na conexão'}
                </p>
                {testResult.success ? (
                  <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                    {testResult.verified_name && <p>Nome verificado: <strong>{testResult.verified_name}</strong></p>}
                    {testResult.phone_number && <p>Telefone: <strong>{testResult.phone_number}</strong></p>}
                    {testResult.quality_rating && <p>Qualidade: <strong>{testResult.quality_rating}</strong></p>}
                  </div>
                ) : (
                  <p className="text-sm text-red-600 mt-1">{testResult.error}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instruções */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Como configurar</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
            <li>Acesse o <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta for Developers</a> e crie um App do tipo "Business"</li>
            <li>Adicione o produto "WhatsApp" ao seu app</li>
            <li>No painel do WhatsApp, copie o <strong>Phone Number ID</strong> e o <strong>WhatsApp Business Account ID</strong></li>
            <li>Gere um <strong>Access Token permanente</strong> via System User no Business Manager</li>
            <li>Configure o <strong>Webhook URL</strong> acima no painel da Meta com o Verify Token desejado</li>
            <li>Inscreva-se nos campos: <code className="bg-gray-100 px-1 rounded">messages</code></li>
            <li>Salve a configuração e teste a conexão</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
